import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials enables the httpOnly `fyllr_salon` cookie to be sent on
// same-origin requests to protected routes (POST /gaps, DELETE /gaps/{id},
// GET /salon-auth/me, etc.).
export const api = axios.create({ baseURL: API, withCredentials: true });

// Salon content submissions (photos/videos for FYLLR socials)
export async function uploadSalonContent(formData, onUploadProgress) {
  const { data } = await api.post("/salon/content/upload", formData, { onUploadProgress });
  return data;
}
export async function getMyContent() {
  const { data } = await api.get("/salon/content");
  return data;
}
export const contentFileUrl = (storagePath) => `${API}/content/file/${storagePath}`;

// Admin content review + platform settings
export async function getAdminContent() {
  const { data } = await api.get("/admin/dashboard/content");
  return data;
}
export async function updateContentStatus(id, status) {
  const { data } = await api.patch(`/admin/dashboard/content/${id}`, { status });
  return data;
}
export async function deleteContentSubmission(id) {
  const { data } = await api.delete(`/admin/dashboard/content/${id}`);
  return data;
}
export async function getPlatformSettings() {
  const { data } = await api.get("/admin/dashboard/settings");
  return data;
}
export async function savePlatformSettings(payload) {
  const { data } = await api.post("/admin/dashboard/settings", payload);
  return data;
}

// Client (customer) auth — unified email/password + Google
export async function clientRegister(payload) {
  const { data } = await api.post("/client-auth/register", payload);
  return data;
}
export async function clientLogin(email, password) {
  const { data } = await api.post("/client-auth/login", { email, password });
  return data;
}
export async function clientGoogleSession(sessionId, options = {}) {
  const { data } = await api.post("/client-auth/session", { session_id: sessionId, ...options });
  return data;
}
export async function clientMe() {
  const { data } = await api.get("/client-auth/me");
  return data;
}
export async function getClientBookings() {
  const { data } = await api.get("/client-auth/bookings");
  return data;
}
export async function getLeaderboard() {
  const { data } = await api.get("/leaderboard");
  return data;
}
export async function partnerBillingCheckout(payload) {
  const { data } = await api.post("/partner-billing/checkout", payload);
  return data;
}
export async function partnerBillingStatus(sessionId) {
  const { data } = await api.get(`/partner-billing/status/${sessionId}`);
  return data;
}
export async function partnerBillingMe(salonSlug) {
  const { data } = await api.get(`/partner-billing/me/${salonSlug}`);
  return data;
}
export async function partnerBillingCancel(salonSlug) {
  const { data } = await api.post("/partner-billing/cancel", { salon_slug: salonSlug });
  return data;
}
export async function cancelClientBooking(id) {
  const { data } = await api.post(`/client-auth/bookings/${id}/cancel`);
  return data;
}
export async function clientLogout() {
  const { data } = await api.post("/client-auth/logout");
  return data;
}

// Salon magic-link auth
export async function requestSalonMagicLink(email) {
  const { data } = await api.post("/salon-auth/request", { email });
  return data;
}

export async function verifySalonMagicLink(token) {
  const { data } = await api.post("/salon-auth/verify", { token });
  return data;
}

// Salon password auth
export async function salonPasswordLogin(email, password) {
  const { data } = await api.post("/salon-auth/login", { email, password });
  return data;
}

export async function salonSetPassword(password) {
  const { data } = await api.post("/salon-auth/set-password", { password });
  return data;
}

// Stripe Connect
export async function salonConnectOnboard() {
  const { data } = await api.post("/salon/connect/onboard");
  return data;
}

export async function salonConnectStatus() {
  const { data } = await api.get("/salon/connect/status");
  return data;
}

export async function getSalonAnalytics(days = 30) {
  const { data } = await api.get(`/salon/analytics?days=${days}`);
  return data;
}

// Session Manager
export async function listSalonSessions() {
  const { data } = await api.get("/salon/sessions");
  return data;
}

export async function createSessionTemplate(payload) {
  const { data } = await api.post("/salon/sessions", payload);
  return data;
}

export async function updateSessionTemplate(id, payload) {
  const { data } = await api.put(`/salon/sessions/${id}`, payload);
  return data;
}

export async function deleteSessionTemplate(id) {
  const { data } = await api.delete(`/salon/sessions/${id}`);
  return data;
}

export async function listSessionInstances(templateId) {
  const { data } = await api.get(`/salon/sessions/${templateId}/instances`);
  return data;
}

export async function adjustInstanceCapacity(instanceId, delta) {
  const { data } = await api.patch(`/salon/sessions/instances/${instanceId}/capacity`, { capacity_delta: delta });
  return data;
}

export async function listAvailableSessions() {
  const { data } = await api.get("/sessions/available");
  return data;
}

export async function bookSessionSpots(instanceId, payload) {
  const { data } = await api.post(`/sessions/${instanceId}/book`, payload);
  return data;
}

export async function createSessionCheckout(payload) {
  const { data } = await api.post("/sessions/checkout", payload);
  return data;
}

export async function reserveSession(payload) {
  const { data } = await api.post("/sessions/reserve", payload);
  return data;
}

export async function getMe() {
  const { data } = await api.get("/salon-auth/me");
  return data;
}

export async function getAdminDashboardData(section) {
  const { data } = await api.get(`/admin/dashboard/${section}`);
  return data;
}

export async function getAdminInsights() {
  const { data } = await api.get("/admin/dashboard/insights");
  return data;
}

export async function getSeoPage(categorySlug, suburbSlug, timeSlug) {
  const suffix = timeSlug ? `/${timeSlug}` : "";
  const { data } = await api.get(`/seo/pages/${categorySlug}/${suburbSlug}${suffix}`);
  return data;
}
export async function getMelbourneSeoPage(timeSlug) { const { data } = await api.get(`/seo/melbourne/${timeSlug}`); return data; }
export async function getEditorialGuide(slug) { const { data } = await api.get(`/seo/guides/${slug}`); return data; }

export async function getAdminSeoOverview() { const { data } = await api.get("/admin/seo/overview"); return data; }
export async function saveAdminSeoSettings(payload) { const { data } = await api.post("/admin/seo/settings", payload); return data; }
export async function getGscStatus() { const { data } = await api.get("/admin/seo/gsc/status"); return data; }
export async function getGscProperties() { const { data } = await api.get("/admin/seo/gsc/properties"); return data; }
export async function startGscConnect() { const { data } = await api.post("/admin/seo/gsc/connect"); return data; }
export async function submitGscSitemap(payload) { const { data } = await api.post("/admin/seo/gsc/sitemaps", payload); return data; }
export async function getGscPerformance(params) { const { data } = await api.get("/admin/seo/gsc/performance", { params }); return data; }

export async function getAdminClients() {
  const { data } = await api.get("/admin/dashboard/clients");
  return data;
}

export async function getAdminClientProfile(clientId) {
  const { data } = await api.get(`/admin/dashboard/clients/${clientId}`);
  return data;
}

export async function getAdminOverview(params = {}) {
  const { data } = await api.get("/admin/dashboard/overview", { params });
  return data;
}

export async function getAdminSubscriptions() {
  const { data } = await api.get("/admin/dashboard/subscriptions");
  return data;
}

export async function updateAdminSubscriptionStatus(salonSlug, status) {
  const { data } = await api.patch(`/admin/dashboard/subscriptions/${salonSlug}`, { status });
  return data;
}

export async function requestAdminSubscriptionFollowUp(salonSlug) {
  const { data } = await api.post(`/admin/dashboard/subscriptions/${salonSlug}/follow-up`);
  return data;
}

export async function getAdminReporting(params = {}) {
  const { data } = await api.get("/admin/dashboard/reporting", { params });
  return data;
}

export async function getAdminIntegrations() {
  const { data } = await api.get("/admin/dashboard/integrations");
  return data;
}

export async function getPartnerCRM(params = {}) {
  const { data } = await api.get("/admin/dashboard/partner-crm", { params });
  return data;
}

export async function updatePartnerCRM(partnerId, payload) {
  const { data } = await api.patch(`/admin/dashboard/partner-crm/${partnerId}`, payload);
  return data;
}

export async function createPartnerTask(partnerId, payload) {
  const { data } = await api.post(`/admin/dashboard/partner-crm/${partnerId}/tasks`, payload);
  return data;
}

export async function updatePartnerTask(partnerId, taskId, payload) {
  const { data } = await api.patch(`/admin/dashboard/partner-crm/${partnerId}/tasks/${taskId}`, payload);
  return data;
}

export async function createPartnerCommunication(partnerId, payload) {
  const { data } = await api.post(`/admin/dashboard/partner-crm/${partnerId}/communications`, payload);
  return data;
}

export async function getAdminListings(params = {}) {
  const { data } = await api.get("/admin/dashboard/listings", { params });
  return data;
}

export async function createAdminListing(payload) {
  const { data } = await api.post("/admin/dashboard/listings", payload);
  return data;
}

export async function updateAdminListing(listingId, payload) {
  const { data } = await api.put(`/admin/dashboard/listings/${listingId}`, payload);
  return data;
}

export async function duplicateAdminListing(listingId) {
  const { data } = await api.post(`/admin/dashboard/listings/${listingId}/duplicate`);
  return data;
}

export async function copyAdminSessionsNextWeek() {
  const { data } = await api.post("/admin/dashboard/listings/sessions/copy-next-week");
  return data;
}

export async function bulkUpdateAdminListings(listingIds, status) {
  const { data } = await api.post("/admin/dashboard/listings/bulk-status", { listing_ids: listingIds, status });
  return data;
}

export async function deleteAdminRecord(section, recordId) {
  const { data } = await api.delete(`/admin/dashboard/record/${section}/${recordId}`);
  return data;
}

export async function clearAdminTestData() {
  const { data } = await api.post("/admin/dashboard/clear-test-data");
  return data;
}

export async function salonLogout() {
  const { data } = await api.post("/salon-auth/logout");
  return data;
}

export async function updateSalonNotifications(prefs) {
  const { data } = await api.patch("/salon/me/notifications", prefs);
  return data;
}

export async function joinWaitlist(payload) {
  const { data } = await api.post("/waitlist", payload);
  return data;
}

let waitlistRetryQueue = [];

function retryQueue() { return [...waitlistRetryQueue]; }

function writeRetryQueue(items) { waitlistRetryQueue = items.slice(-25); }

function signupRequest(payload) {
  return {
    ...payload,
    source: payload.source || "website",
    request_id: payload.request_id || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`),
  };
}

export async function submitWaitlistWithRetry(payload) {
  const request = signupRequest(payload);
  try {
    const data = await joinWaitlist(request);
    if (data.juno_synced === false) {
      const queue = retryQueue();
      if (!queue.some((item) => item.request_id === request.request_id)) writeRetryQueue([...queue, request]);
      return { data, queued: true };
    }
    return { data, queued: false };
  } catch {
    const queue = retryQueue();
    if (!queue.some((item) => item.request_id === request.request_id)) writeRetryQueue([...queue, request]);
    return { data: null, queued: true };
  }
}

export async function flushWaitlistRetryQueue() {
  const pending = retryQueue();
  if (!pending.length) return;
  const failed = [];
  for (const request of pending) {
    try { await joinWaitlist(request); } catch { failed.push(request); }
  }
  writeRetryQueue(failed);
}

export async function updateWaitlistPreferences(email, prefs, token) {
  const { data } = await api.patch(
    `/waitlist/${encodeURIComponent(email)}/preferences`,
    prefs,
    { params: { t: token } }
  );
  return data;
}

export async function getWaitlistEntry(email, token) {
  // SEC-002: uses the token-scoped endpoint instead of dumping the whole list.
  const { data } = await api.get("/waitlist/entry", {
    params: { email, t: token },
  });
  return data;
}

export async function getOpenings(params = {}) {
  const { data } = await api.get("/openings", { params });
  return data;
}

export async function getWaitlistStats() {
  const { data } = await api.get("/waitlist/stats");
  return data;
}

// Salons
export async function getSalons() {
  const { data } = await api.get("/salons");
  return data;
}

export async function getSalon(slug) {
  const { data } = await api.get(`/salons/${slug}`);
  return data;
}

export async function getSalonRecovery(slug, days = 7) {
  const { data } = await api.get(`/salons/${slug}/recovery`, { params: { days } });
  return data;
}

// Bookings
export async function createBookingCheckout(payload) {
  const { data } = await api.post("/bookings/create-checkout", payload);
  return data;
}

export async function reserveBooking(payload) {
  const { data } = await api.post("/bookings/reserve", payload);
  return data;
}

export async function getSalonPublicProfile(salonSlug) {
  const { data } = await api.get(`/salons/${salonSlug}/public-profile`);
  return data;
}

export async function getMyVenueProfile() {
  const { data } = await api.get("/salon/me/venue-profile");
  return data;
}

export async function updateMyVenueProfile(payload) {
  const { data } = await api.patch("/salon/me/venue-profile", payload);
  return data;
}

export async function uploadVenueProfilePhoto(formData) {
  const { data } = await api.post("/salon/me/venue-profile/photos", formData);
  return data;
}

export async function removeVenueProfilePhoto(index) {
  const { data } = await api.delete(`/salon/me/venue-profile/photos/${index}`);
  return data;
}

export async function exportSalonData() {
  return api.get("/salon/me/export.csv", { responseType: "blob" });
}

export async function getSalonMarketingConsentCount() {
  const { data } = await api.get("/salon/me/marketing-consent-count");
  return data;
}

export async function registerReferrer() {
  const { data } = await api.post("/referrals/register");
  return data;
}

export async function getMyReferrals() {
  const { data } = await api.get("/referrals/me");
  return data;
}

export async function getAdminReferrals() {
  const { data } = await api.get("/admin/dashboard/referrals");
  return data;
}

export async function approveReferralReward(rewardId) {
  const { data } = await api.post(`/admin/dashboard/referrals/rewards/${rewardId}/approve`);
  return data;
}

export async function markReferralRewardPaid(rewardId) {
  const { data } = await api.post(`/admin/dashboard/referrals/rewards/${rewardId}/mark-paid`);
  return data;
}

export const salonProfilePhotoUrl = (salonSlug, photoIndex) => `${API}/salons/${salonSlug}/profile-photos/${photoIndex}`;

// Klaviyo event tracking
export async function trackViewedProduct(payload) {
  const { data } = await api.post("/events/viewed-product", payload);
  return data;
}

export async function getBookingStatus(sessionId) {
  const { data } = await api.get(`/bookings/status/${sessionId}`);
  return data;
}

export async function getPaymentsConfig() {
  const { data } = await api.get("/payments/config");
  return data;
}

export async function createBookingPaymentIntent(payload) {
  const { data } = await api.post("/bookings/payment-intent", payload);
  return data;
}

export async function getBookingPayment(bookingId) {
  const { data } = await api.get(`/bookings/${bookingId}/payment`);
  return data;
}

export async function listBookings(params = {}) {
  const { data } = await api.get("/bookings", { params });
  return data;
}

export async function listSalonBookingRequests() {
  const { data } = await api.get("/salon/me/booking-requests");
  return data;
}

export async function approveSalonBookingRequest(bookingId) {
  const { data } = await api.post(`/salon/me/booking-requests/${bookingId}/approve`);
  return data;
}

export async function declineSalonBookingRequest(bookingId) {
  const { data } = await api.post(`/salon/me/booking-requests/${bookingId}/decline`);
  return data;
}

// Market stats
export async function getMarketStats() {
  const { data } = await api.get("/stats/market");
  return data;
}

// Salon onboarding
export async function submitSalonOnboarding(payload) {
  const { data } = await api.post("/salon-onboarding", payload);
  return data;
}

export async function uploadSalonOnboardingPhoto(draftId, file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/salon-onboarding/photos", formData, { params: { draft_id: draftId } });
  return data;
}

export async function generateSalonBio(payload, onToken) {
  const response = await fetch(`${API}/salon-onboarding/bio-assist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!response.ok || !response.body) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail?.detail || "AI writing assistance is unavailable right now.");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  let generated = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, { stream: true });
    const events = buffered.split("\n\n");
    buffered = events.pop() || "";
    events.forEach((event) => {
      const tokenLine = event.split("\n").find((line) => line.startsWith("data: "));
      if (!tokenLine || event.startsWith("event: error")) return;
      try {
        const token = JSON.parse(tokenLine.slice(6)).token;
        if (token) { generated += token; onToken?.(generated); }
      } catch { /* ignore stream framing */ }
    });
  }
  if (!generated.trim()) throw new Error("AI writing assistance did not return a paragraph. Please try again.");
  return generated.trim();
}

// Gaps (salon-posted openings)
export async function createGap(payload) {
  const { data } = await api.post("/gaps", payload);
  return data;
}

export async function deleteGap(id) {
  const { data } = await api.delete(`/gaps/${id}`);
  return data;
}

// ---- Phase 2A: Salon services + availability CRUD ----
export async function listSalonServices({ includeArchived = false } = {}) {
  const { data } = await api.get("/salon/me/services", {
    params: includeArchived ? { include_archived: true } : {},
  });
  return data;
}

export async function createSalonService(payload) {
  const { data } = await api.post("/salon/me/services", payload);
  return data;
}

export async function updateSalonService(id, payload) {
  const { data } = await api.patch(`/salon/me/services/${id}`, payload);
  return data;
}

export async function deleteSalonService(id) {
  const { data } = await api.delete(`/salon/me/services/${id}`);
  return data;
}

export async function listSalonAvailability() {
  const { data } = await api.get("/salon/me/availability");
  return data;
}

export async function createSalonAvailability(payload) {
  const { data } = await api.post("/salon/me/availability", payload);
  return data;
}

export async function updateSalonAvailability(id, payload) {
  const { data } = await api.patch(`/salon/me/availability/${id}`, payload);
  return data;
}

export async function deleteSalonAvailability(id) {
  const { data } = await api.delete(`/salon/me/availability/${id}`);
  return data;
}

export async function getSalonEarnings() {
  const { data } = await api.get("/salon/me/earnings");
  return data;
}

export async function salonCancelPaidBooking(id) {
  const { data } = await api.post(`/salon/me/bookings/${id}/cancel`);
  return data;
}

export async function salonMarkNoShow(id) {
  const { data } = await api.post(`/salon/me/bookings/${id}/no-show`);
  return data;
}

export async function getClientNotifications() {
  const { data } = await api.get("/client-auth/notifications");
  return data;
}

export async function markClientNotificationsRead() {
  const { data } = await api.post("/client-auth/notifications/read");
  return data;
}

export async function getSalonNotifications() {
  const { data } = await api.get("/salon/me/notifications");
  return data;
}

export async function markSalonNotificationsRead() {
  const { data } = await api.post("/salon/me/notifications/read");
  return data;
}

export async function getDealAlerts() {
  const { data } = await api.get("/client-auth/deal-alerts");
  return data;
}

export async function saveDealAlerts(payload) {
  const { data } = await api.put("/client-auth/deal-alerts", payload);
  return data;
}
